/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-authentication
 * Authentication utilities for Privy integration
 */
import { usePrivy } from '@privy-io/react-auth';

/**
 * Get the access token for authenticated API requests
 * Returns null if not authenticated
 */
export async function getAccessToken(): Promise<string | null> {
  // Note: This function should be called from within a component
  // that has access to the Privy context. For standalone usage,
  // the token should be passed from the hook.
  return null;
}

/**
 * Hook to get the current access token
 * Memoizes the token and handles refresh
 */
export function useAccessToken() {
  const { getAccessToken: privyGetAccessToken, authenticated } = usePrivy();

  const getToken = async (): Promise<string | null> => {
    if (!authenticated) return null;
    try {
      return await privyGetAccessToken();
    } catch {
      return null;
    }
  };

  return { getToken, isAuthenticated: authenticated };
}

/**
 * Check if user has completed social auth (not just wallet connection)
 * Social auth includes: email, Google, passkey
 */
export function isSocialAuth(user: { linkedAccounts?: Array<{ type: string }> } | null): boolean {
  if (!user?.linkedAccounts) return false;

  const socialTypes = ['email', 'google_oauth', 'passkey'];
  return user.linkedAccounts.some((account) => socialTypes.includes(account.type));
}

/**
 * Check if user has an embedded wallet (created via social auth)
 */
export function hasEmbeddedWallet(
  user: { linkedAccounts?: Array<{ type: string; walletClientType?: string }> } | null
): boolean {
  if (!user?.linkedAccounts) return false;

  return user.linkedAccounts.some(
    (account) => account.type === 'wallet' && account.walletClientType === 'privy'
  );
}
